import { useEffect, useState } from "react";

import { RingProgress } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { DashboardService } from "services";
import { DashboardTeamLeadDailyKpiMeterResponse } from "types";

import classes from "./TeamKpisSection.module.css";
import shellClasses from "./SectionShell.module.css";

interface TeamKpisSectionProps {
  className?: string;
  selectedAgentIds?: number[] | null;
}

interface TeamKpiMetricDefinition {
  labelKey: string;
  metricKeys: readonly string[];
  color: string;
}

interface TeamKpiMetric extends Omit<TeamKpiMetricDefinition, "metricKeys"> {
  value: number;
  target: number;
}

const TEAM_KPI_METRICS: readonly TeamKpiMetricDefinition[] = [
  {
    labelKey: "dashboard.kpis.metrics.approved",
    metricKeys: ["approved"],
    color: "#d4c84a",
  },
  {
    labelKey: "dashboard.kpis.metrics.disbursed",
    metricKeys: ["disbursedCount"],
    color: "#b24545",
  },
  {
    labelKey: "dashboard.kpis.metrics.leads",
    metricKeys: ["leadsInBank"],
    color: "#71bf44",
  },
] as const;

const createDefaultMetrics = (): TeamKpiMetric[] =>
  TEAM_KPI_METRICS.map((metric) => ({
    labelKey: metric.labelKey,
    value: 0,
    target: 0,
    color: metric.color,
  }));

const mapKpiMetersToMetrics = (
  metrics: readonly TeamKpiMetricDefinition[],
  meters: DashboardTeamLeadDailyKpiMeterResponse[],
): TeamKpiMetric[] => {
  const metersByMetric = meters.reduce<Record<string, DashboardTeamLeadDailyKpiMeterResponse>>(
    (acc, item) => {
      acc[item.metric] = item;
      return acc;
    },
    {},
  );

  return metrics.map((metric) => {
    const data = metric.metricKeys.map((key) => metersByMetric[key]).find(Boolean);

    return {
      labelKey: metric.labelKey,
      value: typeof data?.value === "number" ? data.value : 0,
      target: typeof data?.target === "number" ? Math.ceil(data.target) : 0,
      color: metric.color,
    };
  });
};

const getProgressValue = (value: number, target: number) => {
  if (target <= 0) {
    return 0;
  }

  return Math.min((value / target) * 100, 100);
};

const TeamKpiMetricCard = ({ labelKey, value, target, color }: TeamKpiMetric) => {
  const { t } = useTranslation();

  return (
    <div className={classes.metric}>
      <div className={classes.ring}>
        <RingProgress
          size={128}
          thickness={8}
          roundCaps
          sections={[{ value: getProgressValue(value, target), color }]}
          rootColor="#d9dee4"
        />
        <div className={classes.ringValue}>{`${value}/${target}`}</div>
      </div>

      <Text className={classes.metricLabel}>{t(labelKey)}</Text>
    </div>
  );
};

const TeamKpisSection = ({ className, selectedAgentIds }: TeamKpisSectionProps) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<TeamKpiMetric[]>(createDefaultMetrics());

  useEffect(() => {
    let mounted = true;
    const params = selectedAgentIds?.length
      ? { agentIds: selectedAgentIds }
      : undefined;

    const loadKpis = async () => {
      try {
        const data = await DashboardService.getTeamLeadDailyKpis(params);

        if (!mounted) {
          return;
        }

        setMetrics(mapKpiMetersToMetrics(TEAM_KPI_METRICS, data?.meters ?? []));
      } catch {
        if (!mounted) {
          return;
        }

        setMetrics(createDefaultMetrics());
      }
    };

    loadKpis();

    return () => {
      mounted = false;
    };
  }, [selectedAgentIds]);

  return (
    <section className={[shellClasses.root, className].filter(Boolean).join(" ")}>
      <div className={shellClasses.header}>
        <Text className={shellClasses.title}>{t("dashboard.kpis.daily")}</Text>
      </div>

      <div className={shellClasses.body}>
        <div className={classes.metrics}>
          {metrics.map((metric) => (
            <TeamKpiMetricCard key={metric.labelKey} {...metric} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamKpisSection;
